/* ============================================================================
   CAPA DE MIS PUNTOS

   Dibuja en el mapa la base de puntos del operador: lo que importa de un KMZ,
   lo que marcó en el mapa y lo que cargó a mano.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ ESTA CAPA

   Los puntos se guardaban y se listaban en una tabla, y **nunca se veían en el
   mapa**. Con 41 estructuras de una línea de transmisión, una tabla de 41
   filas de coordenadas no dice por dónde pasa la línea, cuál está cerca de
   cuál, ni dónde conviene poner el acopio.

   Para planificar hay que verlos sobre el terreno.

   ══════════════════════════════════════════════════════════════════════════
   DECISIONES

   · **El rótulo aparece con el zoom**, como los puntos de las aerovías. A zoom
     bajo, 41 rótulos tapan el terreno —que es lo que un piloto de helicóptero
     más necesita ver— y a zoom alto hacen falta para saber cuál es cuál.

   · **El marcador se puede tocar con el dedo.** El símbolo mide 22 px; el área
     sensible, 36. Un punto que no se puede abrir no sirve para planificar.

   · **El popup lleva la coordenada en grados, minutos y segundos**, que es como
     se lee en la carta y como se dice por radio.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import { posicionEnGms } from "../../domain/coordenadas.js";
import {
  puntosDibujables, tipoDePunto, iconoDePunto, conteoPorTipo,
} from "../../domain/tiposDePunto.js";

/** Dónde se recuerda si el piloto quiere verlos. */
export const CLAVE_VISIBLE = "hcMisPuntosEnMapa";

export function misPuntosVisibles() {
  try { return localStorage.getItem(CLAVE_VISIBLE) === "si"; } catch { return false; }
}

export function guardarVisibles(v) {
  try { localStorage.setItem(CLAVE_VISIBLE, v ? "si" : "no"); } catch { /* modo privado */ }
}

/** Desde qué zoom se ven los rótulos. */
export const ZOOM_ROTULOS = 11;

/** Lo que dice el globo de un punto. */
export function contenidoDelPopup(punto) {
  const t = tipoDePunto(punto);
  const gms = posicionEnGms(punto.lat, punto.lon);

  const lineas = [
    `<strong>${escapar(punto.nombre || "PUNTO")}</strong>`,
    `<div class="hc-mispuntos__tipo">${escapar(t.nombre)}</div>`,
    `<div class="hc-mispuntos__gms">${escapar(gms || "")}</div>`,
  ];

  /* De dónde salió el punto. Uno que vino de un KMZ de un tercero no merece la
     misma confianza que uno que el piloto midió, y a los seis meses los dos se
     ven iguales si no se dice. */
  if (punto.archivo) {
    lineas.push(`<div class="hc-mispuntos__origen">de ${escapar(punto.archivo)}</div>`);
  } else if (punto.origen) {
    lineas.push(`<div class="hc-mispuntos__origen">cargado desde: ${escapar(punto.origen)}</div>`);
  }

  /* Solo se dice cuando se SABE. Un punto sin clasificar no afirma nada: un
     claro sin clasificar puede ser perfectamente aterrizable, y marcarlo como
     «no» haría descartar una opción real. */
  if (t.operativo === true) {
    lineas.push('<div class="hc-mispuntos__posar">Se puede posar · la aplicación guarda la posición, no que esté habilitado</div>');
  } else if (t.operativo === false) {
    lineas.push('<div class="hc-mispuntos__noposar">No es un lugar para posar</div>');
  }

  return lineas.join("");
}

/** Escapa el texto del operador: un nombre con `<` rompería el globo. */
function escapar(texto) {
  return String(texto ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Crea la capa con todos los puntos dibujables.
 *
 * **Sin puntos devuelve `null`, no una capa vacía.** Una capa vacía montada en
 * el mapa hace creer al interruptor que hay algo encendido, y el piloto queda
 * buscando puntos que no existen.
 */
export function crearCapaMisPuntos(L, puntos, { alElegir } = {}) {
  const dibujables = puntosDibujables(puntos);
  if (dibujables.length === 0) return null;

  const grupo = L.layerGroup();
  const marcas = [];

  for (const p of dibujables) {
    const tamano = 22;
    const area = 36;

    const icono = L.divIcon({
      className: "hc-mispuntos__marca",
      html: `<div class="hc-mispuntos__simbolo">${iconoDePunto(p, { tamano })}</div>`
        + `<span class="hc-mispuntos__rotulo">${escapar(p.nombre || "")}</span>`,
      /* El área sensible es más grande que el símbolo: dos píxeles son
         imposibles de acertar con el dedo, y con guante peor. */
      iconSize: [area, area],
      iconAnchor: [area / 2, area / 2],
    });

    const marca = L.marker([p.lat, p.lon], { icon: icono, keyboard: false });
    marca.bindPopup(contenidoDelPopup(p));
    if (alElegir) marca.on("click", () => alElegir(p));

    marca.addTo(grupo);
    marcas.push(marca);
  }

  /* Lo que el mapa necesita saber sin volver a recorrer la lista. */
  grupo.hcPuntos = dibujables;
  grupo.hcMarcas = marcas;
  grupo.hcConteo = conteoPorTipo(dibujables);

  return grupo;
}

/**
 * Enciende o apaga los rótulos según el zoom.
 *
 * Se esconden con una clase, NO se recrean los marcadores: rehacerlos en cada
 * movimiento haría parpadear el mapa entero. Y un rótulo escondido sigue
 * estando, así que el símbolo se puede tocar igual.
 */
export function ajustarRotulosAlZoom(capa, zoom) {
  if (!capa?.hcMarcas) return;
  const mostrar = zoom >= ZOOM_ROTULOS;

  for (const m of capa.hcMarcas) {
    const el = m.getElement?.();
    if (!el) continue;
    el.classList.toggle("hc-mispuntos__marca--con-rotulo", mostrar);
  }
}

/**
 * El rectángulo que contiene todos los puntos, para encuadrar el mapa.
 *
 * Es lo que hace útil el botón «ver en el mapa»: encenderlos sin mover la
 * vista deja al piloto mirando un mapa vacío con los puntos fuera de pantalla,
 * y pensando que la función no anda.
 *
 * Con UN punto devuelve null: no hay rectángulo, y ahí lo que corresponde es
 * centrar, no encuadrar.
 */
export function encuadreDe(puntos) {
  const d = puntosDibujables(puntos);
  if (d.length === 0) return null;

  const lats = d.map((p) => p.lat);
  const lons = d.map((p) => p.lon);
  const sur = Math.min(...lats);
  const norte = Math.max(...lats);
  const oeste = Math.min(...lons);
  const este = Math.max(...lons);

  if (d.length === 1 || (norte === sur && este === oeste)) {
    return { centro: [d[0].lat, d[0].lon], limites: null, cuantos: d.length };
  }

  return {
    centro: [(sur + norte) / 2, (oeste + este) / 2],
    limites: [[sur, oeste], [norte, este]],
    cuantos: d.length,
  };
}
